import React, { useState, useEffect, useRef, useMemo } from 'react'
import { WheelPickerWrapper, WheelPicker } from '@ncdai/react-wheel-picker'
import '@ncdai/react-wheel-picker/style.css'
import './WheelSelector.css'

export function WheelSelector({
  options, // can be number[] or option object array
  initialValue,
  onChange,
  visibleCount = 20, // must be multiple of 4 (per docs) :contentReference[oaicite:0]{index=0}
  optionItemHeight = 40,
  width = 200,

  // New props for suffix overlay
  formatter = (val) => String(val),
  suffixText = '', // e.g. “years old”
  suffixGap = 6, // gap px between main label and suffix
}) {
  // Normalize options to the shape { label, value: string }
  const normalizedOptions = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      // If it's an array of numbers or strings, convert
      if (typeof options[0] === 'number' || typeof options[0] === 'string') {
        return options.map((o) => ({
          label: formatter(o),
          value: String(o),
        }))
      } else if (
        typeof options[0] === 'object' &&
        options[0] !== null &&
        'label' in options[0] &&
        'value' in options[0]
      ) {
        // Already in correct shape
        return options
      }
    }
    // fallback
    return [
      { label: 'Apple', value: 'apple' },
      { label: 'Banana', value: 'banana' },
      { label: 'Cherry', value: 'cherry' },
      { label: 'Date', value: 'date' },
      { label: 'Elderberry', value: 'elderberry' },
    ]
  }, [options, formatter])

  // Determine default value (string form)
  const defaultStringVal = (() => {
    if (
      initialValue != null &&
      normalizedOptions.some((o) => String(o.value) === String(initialValue))
    ) {
      return String(initialValue)
    }
    return normalizedOptions[0].value
  })()

  const [value, setValue] = useState(defaultStringVal)

  // Refs & state for overlay label width measurement
  const overlayLabelRef = useRef(null)
  const [labelWidth, setLabelWidth] = useState(0)

  useEffect(() => {
    // After value updates, measure the overlay label's width
    if (overlayLabelRef.current) {
      const w = overlayLabelRef.current.offsetWidth
      setLabelWidth(w)
    }
  }, [value, formatter, suffixText])

  const handleValueChange = (newVal) => {
    setValue(newVal)
    // If original options were numeric, pass a number back
    if (Array.isArray(options) && typeof options[0] === 'number') {
      const num = Number(newVal)
      if (!isNaN(num)) {
        onChange && onChange(num)
        return
      }
    }
    // else, pass string
    onChange && onChange(newVal)
  }

  const containerHeight = visibleCount * optionItemHeight

  return (
    <div
      className="demo-wheelpicker-outer"
      style={{
        width: `${width}px`,
        height: `256px`,
        margin: 'auto',
      }}
    >
      <WheelPickerWrapper>
        <WheelPicker
          options={normalizedOptions}
          value={value}
          onValueChange={handleValueChange}
          infinite={false}
          visibleCount={visibleCount}
          optionItemHeight={optionItemHeight}
          dragSensitivity={5}
          scrollSensitivity={10}
          classNames={{
            optionItem: 'demo-picker-option-item',
            highlightWrapper: 'demo-picker-highlight-wrapper',
            highlightItem: 'demo-picker-highlight-item',
          }}
        />
      </WheelPickerWrapper>

      {/* Overlay: show the formatted selected label + suffix */}
      <div
        className="demo-picked-overlay"
        style={{
          // Center the overlay horizontally by using labelWidth and suffix gap
          left: `calc(50% - ${labelWidth / 2}px - ${
            suffixText ? suffixGap / 2 : 0
          }px)`,
          // You might also adjust vertical positioning if needed
        }}
      >
        <span ref={overlayLabelRef} className="demo-picked-main-text">
          {formatter(value)}
        </span>
        {suffixText && (
          <span
            className="demo-picked-suffix-text"
            style={{
              transform: `translateX(${suffixGap}px)`,
            }}
          >
            {suffixText}
          </span>
        )}
      </div>
    </div>
  )
}
